import { supabase } from "@/integrations/supabase/client";

/**
 * Användarimponeringsläge ("Se som användaren").
 *
 * Läget är en spegelvy för systemadmin: appen visar användarens egna projekt,
 * nästa steg och guidning skrivskyddat. Ingen inloggning byts ut och inga
 * behörigheter höjs. Spegling kräver en aktiv, tidsbegränsad supportsession i
 * databasen – utan session går det inte att öppna vyn, och varje visning loggas
 * serverside.
 */

const STORAGE_KEY = "bams_impersonation";
export const IMPERSONATION_EVENT = "bams:impersonation-changed";

export interface ImpersonationState {
  userId: string;
  name: string;
  email: string | null;
  /** Supportsessionen som ger rätt att spegla användaren. */
  sessionId: string;
  startedAt: string;
}

export function getImpersonation(): ImpersonationState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const state = raw ? (JSON.parse(raw) as ImpersonationState) : null;
    return state?.sessionId ? state : null;
  } catch {
    return null;
  }
}

/**
 * Öppnar (eller återanvänder) en supportsession och startar spegelläget.
 * Kastar om databasen inte godkänner sessionen.
 */
export async function startImpersonation(input: {
  userId: string;
  name: string;
  email?: string | null;
  projectId?: string | null;
  reason?: string | null;
}): Promise<ImpersonationState> {
  const { data, error } = await supabase.rpc("support_open_session", {
    p_target_user_id: input.userId,
    p_project_id: input.projectId ?? null,
    p_reason: input.reason ?? "Spegelvy i utvecklarpanelen",
    p_hours: 8,
  });
  if (error || !data) throw error ?? new Error("Ingen supportsession kunde öppnas");

  const state: ImpersonationState = {
    userId: input.userId,
    name: input.name,
    email: input.email ?? null,
    sessionId: data as string,
    startedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent(IMPERSONATION_EVENT));
  return state;
}

export function stopImpersonation() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(IMPERSONATION_EVENT));
}

/** Prenumerera på ändringar i imponeringsläget (även mellan flikar). */
export function subscribeImpersonation(listener: () => void) {
  window.addEventListener(IMPERSONATION_EVENT, listener);
  window.addEventListener("storage", listener);
  return () => {
    window.removeEventListener(IMPERSONATION_EVENT, listener);
    window.removeEventListener("storage", listener);
  };
}
